import type {ModuleVerdict} from '../Engine/verdictSummary';
import type {AssessmentSessionSnapshot, SeverityLevel} from './Assessment';

export type ClinicalIndicatorLevel = 'moderate' | 'mild' | 'low';
export type ClinicalOverallLevel =
  | 'doingOkay'
  | 'someStress'
  | 'difficultTime'
  | 'extraSupport'
  | 'urgent';

export type ClinicalOverallPresentation = {
  level: ClinicalOverallLevel;
  progress: number;
};

export const CLINICAL_MODULE_ORDER = ['y5mdyima', 'y5gayima', 'y5soyima', 'y5ptyima'] as const;

const INDICATOR_RANK: Record<ClinicalIndicatorLevel, number> = {
  low: 0,
  mild: 1,
  moderate: 2,
};

const OVERALL_PRESENTATION: Record<SeverityLevel, ClinicalOverallPresentation> = {
  doing_okay: {level: 'doingOkay', progress: 0.2},
  some_stress: {level: 'someStress', progress: 0.4},
  difficult_time: {level: 'difficultTime', progress: 0.6},
  extra_support: {level: 'extraSupport', progress: 0.8},
  urgent: {level: 'urgent', progress: 1},
};

export const getClinicalIndicatorLevel = (
  verdict: Pick<ModuleVerdict, 'criteriaMet' | 'determined'>,
): ClinicalIndicatorLevel => {
  if (verdict.criteriaMet) return 'moderate';
  return verdict.determined ? 'low' : 'mild';
};

export const isClinicianGuidedAssessment = (
  assessment: Pick<AssessmentSessionSnapshot, 'mode' | 'startedByProvider'>,
): boolean => assessment.mode === 'guided' && Boolean(assessment.startedByProvider);

export const orderClinicalVerdicts = (verdicts: ModuleVerdict[]): ModuleVerdict[] => {
  const orderIndex = new Map<string, number>(
    CLINICAL_MODULE_ORDER.map((target, index) => [target, index]),
  );

  return [...verdicts].sort(
    (left, right) =>
      (orderIndex.get(left.target) ?? CLINICAL_MODULE_ORDER.length) -
      (orderIndex.get(right.target) ?? CLINICAL_MODULE_ORDER.length),
  );
};

export const getTopClinicalVerdict = (verdicts: ModuleVerdict[]): ModuleVerdict | undefined => {
  return orderClinicalVerdicts(verdicts).reduce<ModuleVerdict | undefined>((highest, verdict) => {
    if (!highest) return verdict;

    const highestRank = INDICATOR_RANK[getClinicalIndicatorLevel(highest)];
    const verdictRank = INDICATOR_RANK[getClinicalIndicatorLevel(verdict)];
    return verdictRank > highestRank ? verdict : highest;
  }, undefined);
};

export const getClinicalOverallPresentation = (
  severityLevel: SeverityLevel,
): ClinicalOverallPresentation => OVERALL_PRESENTATION[severityLevel];

export const formatClinicalNote = (template: string, condition: string): string =>
  template.replace('{condition}', condition);
